import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Search,
  Upload,
} from 'lucide-react';
import clsx from 'clsx';
import { Button, Card, TextView } from '@/shims/uiCommonComponents';
import type { Entity, EntityCodeMapping } from '@/types';
import { isIcpCode } from '@/utils/icp';
import { entityCodeMappingApi, entitiesApi } from '@/api/client';
import { useAppStore } from '@/store';

const SOURCE_SYSTEM = 'Excel';

interface MappingFormModalProps {
  mapping?: EntityCodeMapping | null;
  entities: Entity[];
  defaultExternalCode?: string;
  onSave: (data: { source_system: string; external_code: string; entity_id: number }) => Promise<void>;
  onClose: () => void;
}

function MappingFormModal({ mapping, entities, defaultExternalCode, onSave, onClose }: MappingFormModalProps) {
  const [companyNameInput, setCompanyNameInput] = useState(
    mapping ? (mapping.entity_name || mapping.entity_code || '') : ''
  );
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(
    mapping ? entities.find((e) => Number(e.id) === mapping.entity_id) ?? null : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const externalCodeRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const query = companyNameInput.trim().toLowerCase();
  const filteredEntities = query
    ? entities.filter(
        (e) =>
          (e.name || '').toLowerCase().includes(query) ||
          (e.code || '').toLowerCase().includes(query)
      )
    : entities;

  const handleSelectEntity = (e: Entity) => {
    setSelectedEntity(e);
    setCompanyNameInput(e.name || e.code || '');
    setShowSuggestions(false);
  };

  const handleSubmit = async () => {
    const externalCode = externalCodeRef.current?.value?.trim() ?? '';
    if (!externalCode) {
      setError('External code is required');
      return;
    }
    if (!selectedEntity) {
      setError('Please enter and select a company name');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        source_system: SOURCE_SYSTEM,
        external_code: externalCode,
        entity_id: Number(selectedEntity.id),
      });
      onClose();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <TextView variant="heading-2">
            {mapping ? 'Edit Entity Code Mapping' : 'Add Entity Code Mapping'}
          </TextView>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <TextView variant="label-xs" className="block mb-1.5">External Code (ICP Code)</TextView>
            <input
              ref={externalCodeRef}
              type="text"
              defaultValue={mapping?.external_code ?? defaultExternalCode ?? ''}
              placeholder="e.g. ICP_1421, ENT-001"
              className="input-field text-sm w-full"
              autoComplete="off"
            />
          </div>

          <div className="relative" ref={suggestionsRef}>
            <TextView variant="label-xs" className="block mb-1.5">Company Name</TextView>
            <div className="relative">
              <input
                type="text"
                value={companyNameInput}
                onChange={(e) => {
                  setCompanyNameInput(e.target.value);
                  setSelectedEntity(null);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Type company name to search..."
                className="input-field text-sm w-full pr-10"
                autoComplete="off"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {showSuggestions && (
              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {filteredEntities.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">No matching company</div>
                ) : (
                  filteredEntities.slice(0, 10).map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => handleSelectEntity(e)}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                    >
                      <span className="text-gray-900">{e.name}</span>
                      <span className="text-xs text-gray-500 font-mono">{e.code}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                {mapping ? 'Update' : 'Add Mapping'}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function EntityCodeMappingPage() {
  const { addNotification } = useAppStore();
  const [mappings, setMappings] = useState<EntityCodeMapping[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editMapping, setEditMapping] = useState<EntityCodeMapping | null>(null);
  const [defaultExternalCode, setDefaultExternalCode] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [mappingsRes, entitiesRes] = await Promise.all([
        entityCodeMappingApi.list({ page: 1, page_size: 200 }),
        entitiesApi.list({ page: 1, page_size: 500 }),
      ]);
      const mappingItems = Array.isArray(mappingsRes) ? mappingsRes : (mappingsRes?.items ?? []);
      const entityItems = Array.isArray(entitiesRes) ? entitiesRes : (entitiesRes?.items ?? []);
      setMappings(mappingItems);
      setEntities(entityItems);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load entity code mappings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (data: {
    source_system: string;
    external_code: string;
    entity_id: number;
  }) => {
    if (editMapping) {
      await entityCodeMappingApi.update(editMapping.id, data);
      addNotification({
        type: 'success',
        title: 'Mapping Updated',
        message: `"${data.external_code}" now maps to the selected entity.`,
      });
    } else {
      await entityCodeMappingApi.create(data);
      addNotification({
        type: 'success',
        title: 'Mapping Created',
        message: `"${data.external_code}" mapped successfully. Configure before uploading ICP.`,
      });
    }
    setShowModal(false);
    setEditMapping(null);
    await loadData();
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await entityCodeMappingApi.uploadCsv(file, 'FCCS');
      const createdCount = res.created?.length ?? 0;
      const errCount = res.errors?.length ?? 0;
      const skipCount = res.skipped ?? 0;
      addNotification({
        type: 'success',
        title: 'CSV Import Complete',
        message: `Created ${createdCount} mapping(s). Skipped: ${skipCount}. ${errCount ? `Errors: ${errCount}` : ''}`,
      });
      if (errCount > 0 && res.errors?.length) {
        res.errors.slice(0, 3).forEach((msg) =>
          addNotification({ type: 'error', title: 'Import Error', message: msg })
        );
      }
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: apiErr.response?.data?.detail || apiErr.message || 'Failed to import CSV.',
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (m: EntityCodeMapping) => {
    if (!confirm(`Remove mapping "${m.external_code}" → ${m.entity_name || m.entity_code}?`)) return;
    try {
      await entityCodeMappingApi.delete(m.id);
      addNotification({
        type: 'success',
        title: 'Mapping Deleted',
        message: `Mapping for "${m.external_code}" has been removed.`,
      });
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: apiErr.detail || 'Failed to delete mapping.',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <TextView variant="heading-1">Entity Code Mapping</TextView>
          <TextView variant="body-sm">
            ICP codes from trial balance uploads are auto-added here. You can edit or add mappings manually.
          </TextView>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={handleCsvUpload}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || uploading}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload CSV
          </Button>
          <Button
            onClick={() => {
              setEditMapping(null);
              setDefaultExternalCode(undefined);
              setShowModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Mapping
          </Button>
        </div>
      </div>

      {error && (
        <div className="card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <Button variant="secondary" size="sm" onClick={loadData} className="mt-2">
              Retry
            </Button>
          </div>
        </div>
      )}

      {!error && (
        <Card>
          <TextView variant="heading-3" className="mb-4">Mapped Entity Codes</TextView>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header text-left">ICP Code</th>
                  <th className="table-header text-left">Maps to Entity</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="table-cell py-8 text-center text-gray-400">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : !mappings || mappings.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="table-cell py-6 text-center text-gray-500 text-sm">
                      No mappings yet. Click Add Mapping to create one.
                    </td>
                  </tr>
                ) : (
                  mappings.map((m, idx) => (
                    <tr
                      key={m.id ?? `mapping-${idx}`}
                      className={clsx('hover:bg-gray-50/50', isIcpCode(m.external_code) && 'bg-primary-50/50')}
                    >
                      <td className="table-cell font-mono text-gray-900">{m.external_code}</td>
                      <td className="table-cell">
                        <span className="text-gray-900">{m.entity_name || m.entity_code || '—'}</span>
                        {m.entity_code && m.entity_name && m.entity_name !== m.entity_code && !m.entity_name.includes(m.entity_code) && (
                          <span className="text-xs text-gray-500 ml-1">({m.entity_code})</span>
                        )}
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setEditMapping(m); setShowModal(true); }}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m)}
                            className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showModal && (
        <MappingFormModal
          mapping={editMapping}
          entities={entities}
          defaultExternalCode={defaultExternalCode}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditMapping(null);
            setDefaultExternalCode(undefined);
          }}
        />
      )}

    </div>
  );
}
